import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { extend, reduce } from 'lodash';
import { Observable } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
  ENTITY_ACCEPT_VERSION_HEADER,
} from '../auth-data/auth-http.service';
import { IResponseInfo } from '../http/http.data';
import { Dashboard } from '../reports/models/dashboard.model';
import { Report } from '../reports/models/report.model';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { LoggerService } from '../shared/services/logger/logger.service';
import { UrlService } from '../shared/services/url/url.service';
import {
  ReportExpandedViewTypesEnum,
  ReportExportFormatEnum,
  SALES_JOURNAL,
} from './report-data.data';
import { HomeMetrics } from './models/home-metrics.model';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { ExpandedSummary } from './models/expanded-summary.model';
import { List } from 'immutable';

export const DASHBOARD_ACCEPT_VERSION = '7.0.0';
export const REPORTS_ACCEPT_VERSION = '5.1.0';
export const REPORTS_ACCEPT_VERSION_V2 = '5.2.0'; // only used for sales_journal that contains tip column
export const REPORTS_EXPORT_ACCEPT_VERSION = '1.0.0';
export const HOME_METRICS_ACCEPT_VERSION = '7.1.0';

@Injectable()
export class ReportDataService {
  private _baseQueryParams: { [queryParam: string]: string };

  protected dashboardHeaders: any = {
    [ACCEPT_VERSION_HEADER]: DASHBOARD_ACCEPT_VERSION,
  };
  protected reportsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: REPORTS_ACCEPT_VERSION,
  };
  protected reportsHeadersV2: any = {
    [ACCEPT_VERSION_HEADER]: REPORTS_ACCEPT_VERSION_V2,
  };
  protected reportsExportHeaders: any = {
    [ACCEPT_VERSION_HEADER]: REPORTS_EXPORT_ACCEPT_VERSION,
    [ENTITY_ACCEPT_VERSION_HEADER]: REPORTS_ACCEPT_VERSION,
  };
  private _homeMetricsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: HOME_METRICS_ACCEPT_VERSION,
  };

  reportsUrl = '/reports';
  reportsExportUrl = '/exports';

  constructor(
    private _loggerService: LoggerService,
    private _authHttpService: AuthHttpService,
    private _settingsDataStore: SettingsDataStore,
    private _urlService: UrlService
  ) {
    this._settingsDataStore.settings$
      .pipe(
        filter((x) => !!x),
        map(
          (settings: Settings) => settings.documentPresetSettings.currencyCode
        ),
        distinctUntilChanged()
      )
      .subscribe(
        (currencyCode: string) =>
          (this._baseQueryParams = { currency_code: currencyCode })
      );
  }

  getReport(reportType: string, queryParams: ParamMap): Observable<Report> {
    const paramsObj = reduce(
      queryParams.keys,
      (result, paramKey) => {
        result[paramKey] = queryParams.get(paramKey);

        return result;
      },
      {}
    );
    const params = extend({}, this._baseQueryParams, paramsObj);
    const url = `${this.reportsUrl}/${reportType}`;

    this._loggerService.log(
      'getting report data for type:',
      reportType,
      'and params:',
      params,
      'from network @ url:',
      url
    );

    const reportHeader =
      reportType === SALES_JOURNAL
        ? this.reportsHeadersV2
        : this.reportsHeaders;

    return this._authHttpService
      .get(url, params, reportHeader)
      .pipe(map(({ body, ..._ }: IResponseInfo) => new Report(body['report'])));
  }

  getDashboard(params?: any): Observable<Dashboard> {
    const url = `${this.reportsUrl}/dashboards/web`;

    return this._authHttpService
      .get(
        url,
        extend({}, this._baseQueryParams, params || {}),
        this.dashboardHeaders
      )
      .pipe(
        map(
          ({ body, ..._ }: IResponseInfo) => new Dashboard(body['dashboard'])
        ),
        take(1)
      );
  }

  exportReport(
    email: string,
    reportType: string,
    rowCount: number,
    reportExportFormat: ReportExportFormatEnum,
    filterValues: { [filterName: string]: string }
  ): Observable<IResponseInfo> {
    const reportParams = extend(
      { type: reportType },
      this._baseQueryParams,
      { row_count: rowCount },
      filterValues
    );
    const params = {
      report: reportParams,
      mail: {
        recipient: email,
      },
    };
    const queryParams = this._urlService.serializeParams({
      format: reportExportFormat,
    });
    const url = `${this.reportsExportUrl}/report?${queryParams}`;

    this._loggerService.log(
      'sending request to export report of type:',
      reportType,
      'and filter values:',
      filterValues,
      'to email:',
      email
    );

    return this._authHttpService
      .post(url, params, this.reportsExportHeaders, true, 'text')
      .pipe(take(1));
  }

  getHomeMetrics(): Observable<HomeMetrics> {
    const url = `${this.reportsUrl}/home_metrics`;

    this._loggerService.log(
      'getting overview metrics from network @ url:',
      url
    );

    return this._authHttpService
      .get(url, this._baseQueryParams, this._homeMetricsHeaders)
      .pipe(
        map(
          ({ body, ..._ }: IResponseInfo) =>
            new HomeMetrics(body['home_metrics'])
        ),
        take(1)
      );
  }

  getExpandedView(
    types: ReportExpandedViewTypesEnum[],
    currencyCode: string,
    topItemsLimit?: number
  ): Observable<List<ExpandedSummary>> {
    const url = `${this.reportsUrl}/expanded_summary`;

    this._loggerService.log(
      'getting expanded summary from network @ url:',
      url
    );

    const queryParams = {
      ...this._baseQueryParams,
      type: types.join(','),
      currency_code: currencyCode,
      year: new Date().getFullYear(),
    };

    if (topItemsLimit) {
      queryParams['top_items_limit'] = topItemsLimit;
    }

    return this._authHttpService.get(url, queryParams).pipe(
      map(({ body, ..._ }: IResponseInfo) =>
        ExpandedSummary.fromJsonList(body['expanded_summaries'])
      ),
      take(1)
    );
  }
}
